import {
  findRewardBenefitFromHydratedCartEntry,
  isDummyItem,
} from 'features/cart/utils/minimal-cart';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { MenuObjectTypes } from 'enums/menu';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useGetMenuObjectQuery } from 'state/menu/menu-options/hooks/use-get-menu-object-query';
import { IMenuObject } from 'state/menu/types';
import { isCartEntryComboSlot, isCartEntryItemOption } from 'utils/cart/is-cart-type';
import { CartEntryType, ComboSlotUiPattern } from 'utils/cart/types';
import { maybeMapCartEntryToEntryTypeAndId } from 'utils/menu/cart-entries-to-menu-object-id';

export interface ModifierMultiplierMap {
  id: string;
  modifierMultiplier: number;
}

export type ComposedCartItemType = Pick<ILegacyCartEntry, 'name' | 'price' | 'image' | 'type'> & {
  calories?: number;
  modifierMultiplier?: number;
  children?: ComposedCartItemType[];
  modifiers?: ComposedCartItemType[];
};

export interface CartEntryMappingParams {
  cartEntry: ILegacyCartEntry;
  hiddenCartEntryIds: Set<string>;
}

const HIDDEN_UI_PATTERNS = [ComboSlotUiPattern.HIDDEN, ComboSlotUiPattern.COLLAPSED];

/**
 * Sorts the modifiers of a cart entry by their modifier multiplier in descending order.
 * @returns Composed cart item with sorted modifiers
 */
const sortComposedCartItemChildrenByModifierMultiplier = (
  composedCartItem: ComposedCartItemType
): ComposedCartItemType => {
  return {
    ...composedCartItem,
    modifiers: composedCartItem.modifiers?.sort(
      (a, b) => (b.modifierMultiplier ?? 0) - (a.modifierMultiplier ?? 0)
    ),
  };
};

/**
 * Compiles a list of hidden cart entry IDs from a cart entry.
 * @returns {string[]} Sanity IDs of hidden cart entries
 */
const getHiddenSanityIdsFromCartEntry = (cartEntry?: ILegacyCartEntry): string[] => {
  return (cartEntry?.children ?? [])
    .filter(child => child.uiPattern && HIDDEN_UI_PATTERNS.includes(child.uiPattern))
    .map(child => child._id);
};

/**
 * Compiles a list of hidden cart entry IDs from a menu object.
 * @returns {string[]} Sanity IDs of hidden cart entries
 */
const getHiddenSanityIdsFromMenuObject = (menuObject: IMenuObject | null | undefined): string[] => {
  const hiddenCartEntryIds: string[] = [];
  for (const option of menuObject?.options ?? []) {
    if (!option) {
      continue;
    }

    if (
      'uiPattern' in option &&
      HIDDEN_UI_PATTERNS.includes(option.uiPattern as ComboSlotUiPattern)
    ) {
      hiddenCartEntryIds.push(option._id);
    }
  }

  return hiddenCartEntryIds;
};

/**
 * Recursively maps a cart entry to a composed cart item.
 * @returns {ComposedCartItemType} Composed cart item
 */
const getCartEntryChildrenMapForItem = ({
  cartEntry,
  hiddenCartEntryIds,
}: CartEntryMappingParams): ComposedCartItemType => {
  if (
    hiddenCartEntryIds.has(cartEntry.sanityId) ||
    cartEntry.isDummyItem ||
    isDummyItem(cartEntry) ||
    isCartEntryComboSlot(cartEntry) ||
    isCartEntryItemOption(cartEntry)
  ) {
    for (const child of cartEntry.children) {
      return getCartEntryChildrenMapForItem({
        cartEntry: child,
        hiddenCartEntryIds,
      });
    }
  }

  const modifiers: ComposedCartItemType[] = cartEntry.children.flatMap(child => {
    const childrenToMap = child.children.length > 0 ? child.children : [child];
    return childrenToMap.map(grandChild =>
      getCartEntryChildrenMapForItem({
        cartEntry: grandChild,
        hiddenCartEntryIds,
      })
    );
  });

  const result = {
    calories: cartEntry.nutritionWithModifiers?.calories,
    image: cartEntry.image,
    modifierMultiplier: cartEntry.modifierMultiplier?.multiplier,
    name: cartEntry.name,
    price: cartEntry.price,
    type: cartEntry.type,
    modifiers,
  };

  return sortComposedCartItemChildrenByModifierMultiplier(result);
};

/**
 * Recursively maps a combo cart entry to a composed cart item.
 * @returns {ComposedCartItemType} Composed cart item
 */
const getCartEntryChildrenMapForCombo = ({
  cartEntry,
  hiddenCartEntryIds,
}: CartEntryMappingParams): ComposedCartItemType => {
  const { children } = cartEntry;

  const rewardBenefit = findRewardBenefitFromHydratedCartEntry(cartEntry);
  if (rewardBenefit) {
    return getCartEntryChildrenMapForItem({
      cartEntry: {
        ...rewardBenefit,
        image: rewardBenefit.image ?? cartEntry.image,
      },
      hiddenCartEntryIds,
    });
  }

  const result = {
    image: cartEntry.image,
    name: cartEntry.name,
    price: cartEntry.price,
    type: cartEntry.type,
    children: children
      .filter(
        child => !hiddenCartEntryIds.has(child._id) && !child.isDummyItem && !isDummyItem(child)
      )
      .map(child =>
        getCartEntryChildrenMapForItem({
          cartEntry: child,
          hiddenCartEntryIds,
        })
      ),
  };

  return sortComposedCartItemChildrenByModifierMultiplier(result);
};

export const useComposeCartItemUIData = (
  cartEntry: ILegacyCartEntry
): ComposedCartItemType | null => {
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { sanityId, menuObjectType } = maybeMapCartEntryToEntryTypeAndId(cartEntry);
  const legacySanityId = isUnifiedMenuEnabled ? '' : sanityId;

  const { data: menuObject } = useGetMenuObjectQuery({
    sanityId: legacySanityId,
    menuObjectType: menuObjectType as MenuObjectTypes,
  });

  const hiddenCartEntryIds = new Set([
    ...getHiddenSanityIdsFromCartEntry(cartEntry),
    ...(!isUnifiedMenuEnabled ? getHiddenSanityIdsFromMenuObject(menuObject) : []),
  ]);

  switch (cartEntry.type) {
    case CartEntryType.item:
      return getCartEntryChildrenMapForItem({
        cartEntry,
        hiddenCartEntryIds,
      });
    case CartEntryType.offerCombo:
    case CartEntryType.combo:
      return getCartEntryChildrenMapForCombo({
        cartEntry,
        hiddenCartEntryIds,
      });
    default:
      return {
        name: cartEntry.name,
        price: cartEntry.price,
        image: cartEntry.image,
        type: cartEntry.type,
        children: [],
      };
  }
};
